import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';

import { errorMessage } from './budgetTags';
import CardsListToggle, { VIEW_CARDS, VIEW_LIST } from './CardsListToggle';

import { modalOptions } from 'Helpers/modalOptions';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import EditIcon from 'Assets/icons/edit.svg';
import LockIcon from 'Assets/icons/lock-closed.svg';
import TrashIcon from 'Assets/icons/trash.svg';
import WarningTriangleIcon from 'Assets/icons/warning-triangle.svg';

const STATUS_TYPE_SUCCESS = 'success';
const STATUS_TYPE_IN_PROGRESS = 'in_progress';
const STATUS_TYPE_FAILED = 'failed';

export default function TagCategoryTab({ tagCategoryId, showUrl, onTagEdit, refreshKey, isBudgetTag, canManageTags, onCategoryUpdated, onTagsChanged }) {
    const [tagCategory, setTagCategory] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    // Cards/List choice, keyed by tag category so it survives tab switches within
    // the session instead of resetting to the per-codeset default every time.
    // Seeded from meta.default_view on a category's first fetch only — see fetchData.
    const [viewByCategory, setViewByCategory] = useState({});
    const [deletedItem, setDeletedItem] = useState(null);
    const [deletedItemStatus, setDeletedItemStatus] = useState(null);
    const [deletedItemModalElement, setDeletedItemModalElement] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [taggableError, setTaggableError] = useState(null);
    // Items whose taggable PUT is in flight; their checkbox stays disabled until it settles.
    const [pendingTaggableIds, setPendingTaggableIds] = useState(() => new Set());

    const deletedItemModalRef = useRef();

    useEffect(() => {
        deletedItemModalRef && setDeletedItemModalElement(new Modal(deletedItemModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        setSearch('');
        setTaggableError(null);
    }, [tagCategoryId]);

    useEffect(() => {
        tagCategoryId && fetchData();
    }, [tagCategoryId, refreshKey]);

    useEffect(() => {
        if (deletedItem) {
            deletedItemModalElement && deletedItemModalElement.show();
        }
    }, [deletedItem]);

    const fetchData = () => {
        setIsLoading(true);
        const url = showUrl.replace('TAG_CATEGORY_ID', tagCategoryId);
        axios
            .get(url)
            .then(({ data }) => {
                setTagCategory(data);
                // Only seed a default the first time this category is fetched — a
                // refetch (tab revisit, or after add/edit/delete) must not clobber a
                // view the user already picked for this tab.
                setViewByCategory((prev) =>
                    tagCategoryId in prev ? prev : { ...prev, [tagCategoryId]: data?.meta?.default_view === VIEW_LIST ? VIEW_LIST : VIEW_CARDS },
                );
            })
            .finally(() => setIsLoading(false));
    };

    const view = viewByCategory[tagCategoryId] ?? VIEW_CARDS;
    const setView = (newView) => setViewByCategory((prev) => ({ ...prev, [tagCategoryId]: newView }));

    const confirmDeletedItem = (item) => {
        setDeletedItem(null);
        setDeletedItem(item);
    };

    const closeAccountModal = () => {
        deletedItemModalElement && deletedItemModalElement.hide();
    };

    const successDeleteHandler = () => {
        fetchData();
        onTagsChanged && onTagsChanged();
        closeAccountModal();
    };

    const triggerDeleteItem = () => {
        setDeletedItemStatus(STATUS_TYPE_IN_PROGRESS);
        setDeleteError(null);

        axios
            .delete(deletedItem.delete_url)
            .then(() => setDeletedItemStatus(STATUS_TYPE_SUCCESS))
            .catch((error) => {
                setDeleteError(error.response?.data?.message ?? null);
                setDeletedItemStatus(STATUS_TYPE_FAILED);
            });
    };

    const setItemTaggable = (itemId, isTaggable) =>
        setTagCategory((prev) => ({
            ...prev,
            data: (prev?.data || []).map((item) => (item.id === itemId && item.is_manual ? { ...item, is_taggable: isTaggable } : item)),
        }));

    const setPending = (itemId, isPending) =>
        setPendingTaggableIds((prev) => {
            const next = new Set(prev);
            isPending ? next.add(itemId) : next.delete(itemId);
            return next;
        });

    // Optimistic: tick/untick at once, roll back if the server refuses.
    const toggleTaggable = (item) => {
        if (pendingTaggableIds.has(item.id)) {
            return;
        }

        const isTaggable = !item.is_taggable;
        setTaggableError(null);
        setPending(item.id, true);
        setItemTaggable(item.id, isTaggable);

        axios
            .put(item.taggable_url, { is_taggable: isTaggable })
            .then(({ data }) => onCategoryUpdated && onCategoryUpdated(data.data))
            .catch((error) => {
                setItemTaggable(item.id, !isTaggable);
                setTaggableError(errorMessage(error));
            })
            .finally(() => setPending(item.id, false));
    };

    const taggable = isBudgetTag ? { canEdit: !!canManageTags, onToggle: toggleTaggable, isPending: (id) => pendingTaggableIds.has(id) } : null;

    const meta = tagCategory?.meta || {};
    const items = tagCategory?.data || [];
    const query = search.trim().toLowerCase();
    const filteredItems = query
        ? items.filter((item) => (item.name || '').toLowerCase().includes(query) || (item.code || '').toLowerCase().includes(query))
        : items;

    return (
        <div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-mb-4">
                <div className="tw-text-sm tw-text-charcoal-50">{countLine(meta, items.length)}</div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <div className="tw-w-full sm:tw-w-64">
                        <TableSearchInput searchValue={search} handleChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <CardsListToggle value={view} onChange={setView} />
                </div>
            </div>

            {meta.hidden_count > 0 && (
                <div className="tw-mb-4 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-amber-400 tw-bg-amber-50 tw-px-4 tw-py-2 tw-text-sm tw-text-amber-800">
                    <WarningTriangleIcon className="tw-w-4 tw-h-4 tw-shrink-0" />
                    <span>
                        {$t('{{count}} unnamed "New Subcategory" rows are hidden — name them in their owning module to surface here.', {
                            count: meta.hidden_count,
                        })}
                    </span>
                </div>
            )}

            {taggableError && (
                <div className="tw-mb-4">
                    <Alert type="error">{taggableError}</Alert>
                </div>
            )}

            {isLoading ? (
                <LoadingSpinner />
            ) : !items.length ? (
                <EmptyState />
            ) : !filteredItems.length ? (
                <div className="tw-text-center tw-text-charcoal-50 tw-py-10">{$t('No results match your search')}</div>
            ) : view === VIEW_LIST ? (
                <TagCategoryListView items={filteredItems} onDelete={confirmDeletedItem} onTagEdit={onTagEdit} taggable={taggable} />
            ) : (
                <div className="tw-grid tw-grid-cols-3 max-2xl:tw-grid-cols-2 max-lg:tw-grid-cols-1 tw-gap-4">
                    {filteredItems.map((item) => (
                        <TagCategoryTabItem key={item.id} item={item} onDelete={confirmDeletedItem} onTagEdit={onTagEdit} taggable={taggable} />
                    ))}
                </div>
            )}

            <ConfirmationModal
                allowAction
                successHandler={successDeleteHandler}
                closeHandler={closeAccountModal}
                triggerAction={triggerDeleteItem}
                cleanStates={() => {
                    setDeletedItemStatus(null);
                    setDeletedItem(null);
                    setDeleteError(null);
                }}
                modalId={`delete-${deletedItem?.id}`}
                modalRef={deletedItemModalRef}
                modalElement={deletedItemModalElement}
                request={deletedItemStatus === STATUS_TYPE_IN_PROGRESS}
                actionCompleted={deletedItemStatus === STATUS_TYPE_SUCCESS}
                actionFailed={deletedItemStatus === STATUS_TYPE_FAILED}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedItem?.name })}
                actionCompletedMsg={$t('[{{name}}] deleted', { name: deletedItem?.name })}
                actionFailedMsg={deleteError ?? $t('The code could not be deleted')}
                title={$t('Delete {{title}}', { title: deletedItem?.name })}
                cancelButtonText={$t('Close')}
                primaryButtonText={$t('Delete')}
            />
        </div>
    );
}

function countLine(meta, fallbackTotal) {
    const total = meta.total ?? fallbackTotal ?? 0;
    let line = $t('{{count}} items', { count: total });

    if (meta.custom_count > 0) {
        line += ` · ${$t('{{count}} synced', { count: meta.synced_count ?? 0 })} · ${$t('{{count}} custom', {
            count: meta.custom_count,
        })}`;
    }

    return line;
}

const CodeChip = ({ code }) =>
    code ? <span className="tw-shrink-0 tw-bg-blue-15 tw-text-blue tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium">{code}</span> : null;

const SourceChip = ({ item }) =>
    item.is_manual ? (
        <Badge variant="success" size="md">
            {$t('Manual')}
        </Badge>
    ) : (
        <Badge variant="neutral" size="md" icon={<LockIcon className="tw-w-full tw-h-full" />}>
            {item.source}
        </Badge>
    );

const ItemActions = ({ item, onTagEdit, onDelete }) => (
    <div className="tw-flex tw-items-center tw-gap-x-3">
        <button
            type="button"
            onClick={() => onTagEdit(item)}
            className="tw-inline-flex tw-items-center tw-gap-x-1 tw-text-blue tw-text-sm tw-font-medium tw-bg-transparent tw-border-0 tw-p-0"
        >
            <EditIcon className="tw-w-3 tw-h-3" />
            {$t('Edit')}
        </button>
        <button
            type="button"
            onClick={() => onDelete(item)}
            className="tw-inline-flex tw-items-center tw-gap-x-1 tw-text-red tw-text-sm tw-font-medium tw-bg-transparent tw-border-0 tw-p-0"
        >
            <TrashIcon className="tw-w-3 tw-h-3" />
            {$t('Delete')}
        </button>
    </div>
);

// Only manual items are codeset tags, so only they can be marked taggable.
const isTaggableItem = (item, taggable) => !!taggable && item.is_manual;

const isFaded = (item, taggable) => isTaggableItem(item, taggable) && !item.is_taggable;

const TaggableCheckbox = ({ item, taggable, showLabel = true }) => (
    <CheckboxInput
        label={showLabel ? $t('Taggable') : null}
        aria-label={$t('Taggable')}
        checked={!!item.is_taggable}
        disabled={!taggable.canEdit || taggable.isPending(item.id)}
        onChange={() => taggable.onToggle(item)}
    />
);

/** The item's name, linking to its tag view when it is a budget tag. */
const ItemName = ({ item }) =>
    item.view_url ? (
        <a href={item.view_url} className="tw-text-blue hover:tw-underline">
            {item.name}
        </a>
    ) : (
        item.name
    );

const TagCategoryTabItem = ({ item, onDelete, onTagEdit, taggable }) => (
    <div
        className={classNames('tw-border-2 tw-rounded-lg tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-2', {
            'tw-border-green': item.is_manual,
            'tw-border-grey': !item.is_manual,
            'tw-opacity-50': isFaded(item, taggable),
        })}
    >
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
            <h6 className="tw-font-semibold tw-text-charcoal tw-mb-0">
                <ItemName item={item} />
            </h6>
            <CodeChip code={item.code} />
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <SourceChip item={item} />
            <div className="tw-flex tw-items-center tw-gap-x-4">
                {item.is_manual && <ItemActions item={item} onTagEdit={onTagEdit} onDelete={onDelete} />}
                {isTaggableItem(item, taggable) && <TaggableCheckbox item={item} taggable={taggable} />}
            </div>
        </div>
    </div>
);

const TagCategoryListView = ({ items, onDelete, onTagEdit, taggable }) => {
    const hasManual = items.some((item) => item.is_manual);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th>{$t('Name')}</th>
                        <th>{$t('Code')}</th>
                        <th>{$t('Source')}</th>
                        {!!taggable && <th>{$t('Taggable')}</th>}
                        {hasManual && <th className="tw-text-end">{$t('Actions')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <tr key={item.id} className={classNames({ 'tw-opacity-50': isFaded(item, taggable) })}>
                            <td className="tw-font-medium">
                                <ItemName item={item} />
                            </td>
                            <td>
                                <CodeChip code={item.code} />
                            </td>
                            <td>
                                <SourceChip item={item} />
                            </td>
                            {!!taggable && <td>{isTaggableItem(item, taggable) && <TaggableCheckbox item={item} taggable={taggable} showLabel={false} />}</td>}
                            {hasManual && (
                                <td className="tw-text-end">{item.is_manual && <ItemActions item={item} onTagEdit={onTagEdit} onDelete={onDelete} />}</td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const EmptyState = () => (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-12 tw-text-charcoal-50">
        <p className="tw-mb-0 tw-font-medium tw-text-charcoal-75">{$t('This codeset has no items yet')}</p>
        <p className="tw-mb-0 tw-text-sm">{$t('Add a code, or they will appear here once synced from their owning module.')}</p>
    </div>
);
